import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  Button,
  Box,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import { Edit as EditIcon } from "@material-ui/icons";

import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import parsePlanAmount from "../../utils/parsePlanAmount";
import {
  PLAN_RECURRENCES,
  PLAN_BILLING_TYPES,
  RECURRENCE_LABELS,
  BILLING_TYPE_LABELS,
  maxInstallmentCount,
} from "../../utils/planMetadata";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(2),
  },
  fullWidth: {
    width: "100%",
  },
  tableContainer: {
    width: "100%",
    overflowX: "scroll",
    ...theme.scrollbarStyles,
  },
  textfield: {
    width: "100%",
  },
  textRight: {
    textAlign: "right",
  },
  row: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  control: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  buttonContainer: {
    textAlign: "right",
    padding: theme.spacing(1),
  },
}));

// Plans.amount quer dizer duas coisas diferentes dependendo do plano: nos
// planos parcelados no cartao e o valor da PARCELA, em todos os outros e o
// total do CICLO. Era o titulo ("... em ate 6x") que avisava o boss qual dos
// dois digitar -- com o titulo livre, quem avisa e este texto. Digitar o
// numero errado aqui cobra 6x ou 12x a mais (ou a menos) de cliente real.
//
// O pseudo-plano vai SEM `name` de proposito: assim planRecurrence nao cai no
// fallback pelo titulo e o texto sempre descreve o que o Select esta mostrando.
const amountHelperText = (formValues) => {
  const parcelas = maxInstallmentCount({
    recurrence: formValues?.recurrence,
    billingTypes: formValues?.billingTypes,
  });
  if (parcelas) {
    return `Valor da PARCELA mensal — x${parcelas} fecha o contrato`;
  }

  const recurrence = String(formValues?.recurrence || "")
    .trim()
    .toUpperCase();
  if (recurrence === "MENSAL") return "Valor cobrado por mês";
  if (PLAN_RECURRENCES.includes(recurrence)) {
    return "Valor TOTAL do ciclo, cobrado de uma vez";
  }
  return "Selecione a recorrência";
};

export function PlanManagerForm(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();

  const [record, setRecord] = useState({
    name: "",
    users: 0,
    connections: 0,
    queues: 0,
    amount: 0,
    useWhatsapp: true,
    useFacebook: true,
    useInstagram: true,
    useCampaigns: true,
    useSchedules: true,
    useInternalChat: true,
    useExternalApi: true,
    useKanban: true,
    useOpenAi: true,
    useIntegrations: true,
    isPublic: true,
    useWhatsappOfficial: false,
    trial: false,
    trialDays: 3,
    recurrence: "MENSAL",
    billingTypes: ["PIX", "BOLETO", "CREDIT_CARD"],
    wavoip: false,
  });

  useEffect(() => {
    setRecord(initialValue);
  }, [initialValue]);

  const handleSubmit = async (data) => {
    onSubmit(data);
  };

  return (
    <Formik
      enableReinitialize
      className={classes.fullWidth}
      initialValues={record}
      onSubmit={(values, { resetForm }) =>
        setTimeout(() => {
          handleSubmit(values);
          resetForm();
        }, 500)
      }
    >
      {(values) => (
        <Form className={classes.fullWidth}>
          <Grid spacing={1} justifyContent="flex-start" container>
            {/* PUBLICO */}
            <Grid xs={12} sm={6} md={1} xl={1} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="status-selection">{i18n.t("plans.form.public")}</InputLabel>
                <Field
                  as={Select}
                  id="status-selection"
                  label={i18n.t("plans.form.public")}
                  labelId="status-selection-label"
                  name="isPublic"
                  margin="dense"
                >
                  <MenuItem value={true}>Sim</MenuItem>
                  <MenuItem value={false}>Não</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* TRIAL */}
            <Grid xs={12} sm={6} md={1} xl={1} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="trial-selection">Trial</InputLabel>
                <Field
                  as={Select}
                  id="trial-selection"
                  label="Trial"
                  labelId="trial-selection-label"
                  name="trial"
                  margin="dense"
                >
                  <MenuItem value={true}>Sim</MenuItem>
                  <MenuItem value={false}>Não</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* TRIAL DAYS */}
            <Grid xs={12} sm={6} md={1} xl={1} item>
              <Field
                as={TextField}
                label={i18n.t("plans.form.trialDays")}
                name="trialDays"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                type="number"
                inputProps={{ min: 1 }}
                disabled={!values.values.trial}
              />
            </Grid>

            {/* NOME */}
            <Grid xs={12} sm={6} md={3} xl={3} item>
              <Field
                as={TextField}
                label={i18n.t("plans.form.name")}
                name="name"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
              />
            </Grid>

            {/* USUARIOS */}
            <Grid xs={12} sm={6} md={1} item>
              <Field
                as={TextField}
                label={i18n.t("plans.form.users")}
                name="users"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                type="number"
              />
            </Grid>

            {/* CONEXOES */}
            <Grid xs={12} sm={6} md={1} item>
              <Field
                as={TextField}
                label={i18n.t("plans.form.connections")}
                name="connections"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                type="number"
              />
            </Grid>

            {/* FILAS */}
            <Grid xs={12} sm={6} md={1} item>
              <Field
                as={TextField}
                label="Filas"
                name="queues"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                type="number"
              />
            </Grid>

            {/* VALOR */}
            <Grid xs={12} sm={6} md={2} item>
              <Field
                as={TextField}
                label="Valor"
                name="amount"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                type="text"
                helperText={amountHelperText(values.values)}
              />
            </Grid>

            {/* RECORRENCIA */}
            <Grid xs={12} sm={6} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="recurrence-selection">
                  Recorrência
                </InputLabel>
                <Field
                  as={Select}
                  id="recurrence-selection"
                  label="Recorrência"
                  labelId="recurrence-selection-label"
                  name="recurrence"
                  margin="dense"
                >
                  {PLAN_RECURRENCES.map((option) => (
                    <MenuItem key={option} value={option}>
                      {RECURRENCE_LABELS[option]}
                    </MenuItem>
                  ))}
                </Field>
              </FormControl>
            </Grid>

            {/* FORMAS DE PAGAMENTO */}
            <Grid xs={12} sm={6} md={4} item>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={PLAN_BILLING_TYPES}
                getOptionLabel={(option) => BILLING_TYPE_LABELS[option]}
                value={PLAN_BILLING_TYPES.filter((type) =>
                  (values.values.billingTypes || []).includes(type)
                )}
                onChange={(e, selected) =>
                  values.setFieldValue("billingTypes", selected)
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      size="small"
                      label={BILLING_TYPE_LABELS[option]}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Formas de Pagamento"
                    variant="outlined"
                    margin="dense"
                    helperText={
                      (values.values.billingTypes || []).length
                        ? undefined
                        : "Vazio = ninguém consegue pagar este plano"
                    }
                  />
                )}
              />
            </Grid>

            {/* WHATSAPP */}
            <Grid xs={12} sm={6} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useWhatsapp-selection">Whatsapp</InputLabel>
                <Field
                  as={Select}
                  id="useWhatsapp-selection"
                  label="Whatsapp"
                  labelId="useWhatsapp-selection-label"
                  name="useWhatsapp"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* WHATSAPP OFICIAL */}
            <Grid xs={12} sm={6} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useWhatsappOfficial-selection">Whatsapp Oficial</InputLabel>
                <Field
                  as={Select}
                  id="useWhatsappOfficial-selection"
                  label="Whatsapp Oficial"
                  labelId="useWhatsappOfficial-selection-label"
                  name="useWhatsappOfficial"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* FACEBOOK */}
            <Grid xs={12} sm={6} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useFacebook-selection">Facebook</InputLabel>
                <Field
                  as={Select}
                  id="useFacebook-selection"
                  label="Facebook"
                  labelId="useFacebook-selection-label"
                  name="useFacebook"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* INSTAGRAM */}
            <Grid xs={12} sm={6} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useInstagram-selection">Instagram</InputLabel>
                <Field
                  as={Select}
                  id="useInstagram-selection"
                  label="Instagram"
                  labelId="useInstagram-selection-label"
                  name="useInstagram"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* CAMPANHAS */}
            <Grid xs={12} sm={6} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useCampaigns-selection">{i18n.t("plans.form.campaigns")}</InputLabel>
                <Field
                  as={Select}
                  id="useCampaigns-selection"
                  label={i18n.t("plans.form.campaigns")}
                  labelId="useCampaigns-selection-label"
                  name="useCampaigns"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* AGENDAMENTOS */}
            <Grid xs={12} sm={8} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useSchedules-selection">{i18n.t("plans.form.schedules")}</InputLabel>
                <Field
                  as={Select}
                  id="useSchedules-selection"
                  label={i18n.t("plans.form.schedules")}
                  labelId="useSchedules-selection-label"
                  name="useSchedules"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* CHAT INTERNO */}
            <Grid xs={12} sm={8} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useInternalChat-selection">Chat Interno</InputLabel>
                <Field
                  as={Select}
                  id="useInternalChat-selection"
                  label="Chat Interno"
                  labelId="useInternalChat-selection-label"
                  name="useInternalChat"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* API Externa */}
            <Grid xs={12} sm={8} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useExternalApi-selection">API Externa</InputLabel>
                <Field
                  as={Select}
                  id="useExternalApi-selection"
                  label="API Externa"
                  labelId="useExternalApi-selection-label"
                  name="useExternalApi"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* KANBAN */}
            <Grid xs={12} sm={8} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useKanban-selection">CRM</InputLabel>
                <Field
                  as={Select}
                  id="useKanban-selection"
                  label="Kanban"
                  labelId="useKanban-selection-label"
                  name="useKanban"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* OPENAI */}
            <Grid xs={12} sm={8} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useOpenAi-selection">IA</InputLabel>
                <Field
                  as={Select}
                  id="useOpenAi-selection"
                  label="IA"
                  labelId="useOpenAi-selection-label"
                  name="useOpenAi"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* INTEGRACOES */}
            <Grid xs={12} sm={8} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="useIntegrations-selection">Integrações</InputLabel>
                <Field
                  as={Select}
                  id="useIntegrations-selection"
                  label="Integrações"
                  labelId="useIntegrations-selection-label"
                  name="useIntegrations"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>

            {/* WAVOIP */}
            <Grid xs={12} sm={8} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="wavoip-selection">Wavoip</InputLabel>
                <Field
                  as={Select}
                  id="wavoip-selection"
                  label="Wavoip"
                  labelId="wavoip-selection-label"
                  name="wavoip"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>
          </Grid>
          <Grid spacing={2} justifyContent="flex-end" container>
            <Grid sm={3} md={1} item>
              <ButtonWithSpinner
                className={classes.fullWidth}
                loading={loading}
                onClick={() => onCancel()}
                variant="contained"
              >
                {i18n.t("plans.form.clear")}
              </ButtonWithSpinner>
            </Grid>
            {record.id !== undefined ? (
              <Grid sm={3} md={1} item>
                <ButtonWithSpinner
                  className={classes.fullWidth}
                  loading={loading}
                  onClick={() => onDelete(record)}
                  variant="contained"
                  color="secondary"
                >
                  {i18n.t("plans.form.delete")}
                </ButtonWithSpinner>
              </Grid>
            ) : null}
            <Grid sm={3} md={1} item>
              <ButtonWithSpinner
                className={classes.fullWidth}
                loading={loading}
                type="submit"
                variant="contained"
                color="primary"
              >
                {i18n.t("plans.form.save")}
              </ButtonWithSpinner>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}

export function PlansManagerGrid(props) {
  const { records, onSelect, selectedIds, onToggle, onToggleAll } = props;
  const classes = useStyles();
  const selectAllChecked = records.length > 0 && selectedIds && selectedIds.size === records.length;
  const selectAllIndeterminate = selectedIds && selectedIds.size > 0 && selectedIds.size < records.length;

  const renderWhatsapp = (row) => {
    return row.useWhatsapp === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderWhatsappOfficial = (row) => {
    return row.useWhatsappOfficial === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderFacebook = (row) => {
    return row.useFacebook === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderInstagram = (row) => {
    return row.useInstagram === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderCampaigns = (row) => {
    return row.useCampaigns === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderSchedules = (row) => {
    return row.useSchedules === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderInternalChat = (row) => {
    return row.useInternalChat === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderExternalApi = (row) => {
    return row.useExternalApi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderKanban = (row) => {
    return row.useKanban === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderOpenAi = (row) => {
    return row.useOpenAi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderIntegrations = (row) => {
    return row.useIntegrations === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  const renderWavoip = (row) => {
    return row.wavoip === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
  };

  return (
    <Paper className={classes.tableContainer}>
      <Table
        className={classes.fullWidth}
        // size="small"
        padding="none"
        aria-label="a dense table"
      >
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox checked={selectAllChecked} indeterminate={selectAllIndeterminate} onChange={onToggleAll} />
            </TableCell>
            <TableCell align="center" style={{ width: "1%" }}>
              #
            </TableCell>
            <TableCell align="center">{i18n.t("plans.form.trial")}</TableCell>
            <TableCell align="left">{i18n.t("plans.form.name")}</TableCell>
            <TableCell align="center">{i18n.t("plans.form.users")}</TableCell>
            <TableCell align="center">{i18n.t("plans.form.public")}</TableCell>
            <TableCell align="center">{i18n.t("plans.form.connections")}</TableCell>
            <TableCell align="center">Filas</TableCell>
            <TableCell align="center">Valor</TableCell>
            <TableCell align="center">Whatsapp</TableCell>
            <TableCell align="center">Whatsapp Oficial</TableCell>
            <TableCell align="center">Facebook</TableCell>
            <TableCell align="center">Instagram</TableCell>
            <TableCell align="center">{i18n.t("plans.form.campaigns")}</TableCell>
            <TableCell align="center">{i18n.t("plans.form.schedules")}</TableCell>
            <TableCell align="center">Chat Interno</TableCell>
            <TableCell align="center">API Externa</TableCell>
            <TableCell align="center">Kanban</TableCell>
            <TableCell align="center">Talk.Ai</TableCell>
            <TableCell align="center">Integrações</TableCell>
            <TableCell align="center">Wavoip</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row) => (
            <TableRow key={row.id} selected={selectedIds && selectedIds.has(row.id)}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedIds ? selectedIds.has(row.id) : false}
                  onChange={() => onToggle && onToggle(row.id)}
                />
              </TableCell>
              <TableCell align="center" style={{ width: "1%" }}>
                <IconButton onClick={() => onSelect(row)} aria-label="delete">
                  <EditIcon />
                </IconButton>
              </TableCell>
              <TableCell align="center">{row.trialDays || "-"}</TableCell>
              <TableCell align="left">{row.name || "-"}</TableCell>
              <TableCell align="center">{row.users || "-"}</TableCell>
              <TableCell align="center">{row.isPublic ? "Sim" : "Não" || "-"}</TableCell>
              <TableCell align="center">{row.connections || "-"}</TableCell>
              <TableCell align="center">{row.queues || "-"}</TableCell>
              <TableCell align="center">
                {i18n.t("plans.form.money")}{" "}
                {parsePlanAmount(row.amount).toLocaleString("pt-br", {
                  minimumFractionDigits: 2,
                })}
              </TableCell>
              <TableCell align="center">{renderWhatsapp(row)}</TableCell>
              <TableCell align="center">{renderWhatsappOfficial(row)}</TableCell>
              <TableCell align="center">{renderFacebook(row)}</TableCell>
              <TableCell align="center">{renderInstagram(row)}</TableCell>
              <TableCell align="center">{renderCampaigns(row)}</TableCell>
              <TableCell align="center">{renderSchedules(row)}</TableCell>
              <TableCell align="center">{renderInternalChat(row)}</TableCell>
              <TableCell align="center">{renderExternalApi(row)}</TableCell>
              <TableCell align="center">{renderKanban(row)}</TableCell>
              <TableCell align="center">{renderOpenAi(row)}</TableCell>
              <TableCell align="center">{renderIntegrations(row)}</TableCell>
              <TableCell align="center">{renderWavoip(row)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function PlansManager() {
  const classes = useStyles();
  const { list, save, update, remove } = usePlans();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [record, setRecord] = useState({
    name: "",
    users: 0,
    connections: 0,
    queues: 0,
    amount: 0,
    useWhatsapp: true,
    useFacebook: true,
    useInstagram: true,
    useCampaigns: true,
    useSchedules: true,
    useInternalChat: true,
    useExternalApi: true,
    useKanban: true,
    useOpenAi: true,
    useIntegrations: true,
    isPublic: true,
    useWhatsappOfficial: false,
    trial: false,
    trialDays: 3,
    recurrence: "MENSAL",
    billingTypes: ["PIX", "BOLETO", "CREDIT_CARD"],
    wavoip: false,
  });

  useEffect(() => {
    async function fetchData() {
      await loadPlans();
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const planList = await list();
      setRecords(planList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    console.log(data);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error(
        "Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente",
      );
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Não foi possível realizar a operação");
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord({
      id: undefined,
      name: "",
      users: 0,
      connections: 0,
      queues: 0,
      amount: 0,
      useWhatsapp: true,
      useFacebook: true,
      useInstagram: true,
      useCampaigns: true,
      useSchedules: true,
      useInternalChat: true,
      useExternalApi: true,
      useKanban: true,
      useOpenAi: true,
      useIntegrations: true,
      isPublic: true,
      useWhatsappOfficial: false,
      trial: false,
      trialDays: 3,
      recurrence: "MENSAL",
      billingTypes: ["PIX", "BOLETO", "CREDIT_CARD"],
      wavoip: false,
    });
  };

  const handleTogglePlan = (id) => {
    setSelectedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleAllPlans = (e) => {
    if (e.target.checked) setSelectedPlans(new Set(records.map((r) => r.id)));
    else setSelectedPlans(new Set());
  };

  const handleBulkDeleteConfirm = async () => {
    setBulkConfirmOpen(false);
    setLoading(true);
    let ok = 0;
    let failed = 0;
    for (const id of selectedPlans) {
      try {
        await remove(id);
        ok++;
      } catch {
        failed++;
      }
    }
    setSelectedPlans(new Set());
    if (failed === 0) toast.success(`${ok} plano(s) excluído(s) com sucesso!`);
    else toast.warning(`${ok} excluído(s), ${failed} com erro.`);
    await loadPlans();
    setLoading(false);
  };

  const handleSelect = (data) => {
    let useWhatsapp = data.useWhatsapp === false ? false : true;
    let useWhatsappOfficial = data.useWhatsappOfficial === false ? false : true;
    let useFacebook = data.useFacebook === false ? false : true;
    let useInstagram = data.useInstagram === false ? false : true;
    let useCampaigns = data.useCampaigns === false ? false : true;
    let useSchedules = data.useSchedules === false ? false : true;
    let useInternalChat = data.useInternalChat === false ? false : true;
    let useExternalApi = data.useExternalApi === false ? false : true;
    let useKanban = data.useKanban === false ? false : true;
    let useOpenAi = data.useOpenAi === false ? false : true;
    let useIntegrations = data.useIntegrations === false ? false : true;
    let wavoip = data.wavoip === false ? false : true;

    setRecord({
      id: data.id,
      name: data.name || "",
      users: data.users || 0,
      connections: data.connections || 0,
      queues: data.queues || 0,
      amount: data.amount?.toLocaleString("pt-br", { minimumFractionDigits: 2 }) || 0,
      useWhatsapp,
      useWhatsappOfficial,
      useFacebook,
      useInstagram,
      useCampaigns,
      useSchedules,
      useInternalChat,
      useExternalApi,
      useKanban,
      useOpenAi,
      useIntegrations,
      isPublic: data.isPublic,
      trial: data.trial,
      trialDays: data.trialDays,
      recurrence: data.recurrence,
      billingTypes: data.billingTypes || [],
      wavoip,
    });
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <PlanManagerForm
            initialValue={record}
            onDelete={handleOpenDeleteDialog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>
        {selectedPlans.size > 0 && (
          <Grid xs={12} item>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" color="secondary" onClick={() => setBulkConfirmOpen(true)}>
                Excluir selecionados ({selectedPlans.size})
              </Button>
            </Box>
          </Grid>
        )}
        <Grid xs={12} item>
          <PlansManagerGrid
            records={records}
            onSelect={handleSelect}
            selectedIds={selectedPlans}
            onToggle={handleTogglePlan}
            onToggleAll={handleToggleAllPlans}
          />
        </Grid>
      </Grid>
      <ConfirmationModal
        title="Exclusão de Registro"
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        Deseja realmente excluir esse registro?
      </ConfirmationModal>
      <ConfirmationModal
        title="Excluir planos selecionados"
        open={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
      >
        Deseja excluir {selectedPlans.size} plano(s) selecionado(s)?
      </ConfirmationModal>
    </Paper>
  );
}
